export type ArticleBlock = { kind: 'h2' | 'p', text: string }

export type Article = {
    slug: string
    title: string
    /** Page name for the browser tab, kept short enough to fit next to the site name. */
    seoTitle: string
    description: string
    category: string
    /** When the post applies. The inn's posts are seasonal, not dated. */
    date: string
    image: string
    imageAlt: string
    body: ArticleBlock[]
}

export type ArticleAuthor = {
    name: string
    role: string
    avatar: string
}

/** The inn's pages carry no author, so the byline stays hidden. */
export const articleAuthor: ArticleAuthor | null = null

/**
 * Every post is built from the inn's own pages: the discount lift ticket chart,
 * the driving directions and the amenities and area notes. Single source for
 * the home page cards, the news list and the post pages.
 */
export const articles: Article[] = [
    {
        slug: 'discount-killington-lift-tickets',
        title: "Discounted Killington lift tickets with your room",
        seoTitle: "Discount Killington lift tickets",
        description: "Greenbrier Inn guests save up to 29% off Killington window rates on lift tickets valid at Killington and Pico. See the 2025–26 ski and stay prices.",
        category: "Ski & Stay",
        date: "2025–26 season",
        image: '/images/greenbrier/slide-mountain.jpg',
        imageAlt: "Killington ski trails above a hillside of fall foliage",
        body: [
            { kind: 'p', text: "We offer discounted Killington lift tickets to our guests, with savings of up to 29% off the window rate. For the 2025–26 season, the regular window price for an adult ticket is $204." },
            { kind: 'p', text: "Use the prices below as a guide to adding lift tickets to your Greenbrier room. Ticket prices vary on holidays and peak season days, so call us to check availability and get package prices." },
            { kind: 'h2', text: "Non-peak days, Monday to Friday" },
            { kind: 'p', text: "An adult lift ticket added to your room is $145 instead of the $204 window price, a saving of 29%." },
            { kind: 'h2', text: "Saturdays, Sundays and peak days" },
            { kind: 'p', text: "On any Saturday or Sunday, and on peak dates, an adult lift ticket added to your room is $165, a saving of 20%. Peak dates for the 2025–26 season are December 25 to January 4, January 17 to 19, and February 13 to 21." },
            { kind: 'h2', text: "Good to know" },
            { kind: 'p', text: "Tickets are valid any day of the 2025–26 ski season, at Killington and Pico. You will be issued a voucher, which must be redeemed online, and all tickets are required to be loaded on a One Pass." },
            { kind: 'p', text: "Discounted lift tickets can only be purchased with a room reservation and are sold while supplies last. All ticket sales are subject to 6% state tax." },
            { kind: 'h2', text: "How to reserve" },
            { kind: 'p', text: "Lift tickets can be reserved over the phone or at the front desk. Call the hotel at 802-775-1608 for pricing, details and to book your ski and stay package." }
        ]
    },
    {
        slug: 'driving-directions-to-killington',
        title: "Driving directions to the Greenbrier Inn",
        seoTitle: "Driving directions to Killington, VT",
        description: "How to reach the Greenbrier Inn on Route 4 in Killington, Vermont: driving directions from Boston, New York City, Hartford and Montreal, plus Amtrak.",
        category: "Getting here",
        date: "All year",
        image: '/images/greenbrier/inn-exterior-1.jpg',
        imageAlt: "The Greenbrier Inn and Gift Shop on Route 4 in Killington, Vermont",
        body: [
            { kind: 'p', text: "If you are using the internet for directions, our address is 2057 Route 4, Killington, VT 05751. Look for our large blue building on U.S. 4, then come down our driveway: the inn is located behind the gift shop." },
            { kind: 'h2', text: "From the Boston area, approx. 3 hours" },
            { kind: 'p', text: "Take I-93 to just south of Concord, N.H. and exit onto I-89 North. Follow signs to the U.S. 4/Rutland exit in Vermont, then follow U.S. 4 west to Killington. The Greenbrier is located 6.5 miles after the first “Welcome to Killington” sign and 2/10ths of a mile after the second. You will see our large blue building on your right hand side." },
            { kind: 'h2', text: "From New York City, New Jersey and points south, approx. 5 hours" },
            { kind: 'p', text: "Take the Northway (I-87) to Exit 20, the Glens Falls/Queensbury exit, which is also marked Rutland, VT. Take NY 9 North for half a mile to NY 149 East. Go to Fort Ann and turn left onto U.S. 4 north to Whitehall, then continue on U.S. 4 into Vermont." },
            { kind: 'p', text: "Follow U.S. 4 east through Rutland to Killington. The Greenbrier is located 2 miles after the “Welcome to Killington” sign, a large blue building on your left hand side. You can also take Amtrak to Rutland, leaving daily from New York's Penn Station." },
            { kind: 'h2', text: "From the Hartford area, approx. 3 hours" },
            { kind: 'p', text: "Follow I-91 to just north of Bellows Falls, VT. Take Exit 6 (Rutland) onto VT 103 and follow it to VT 100. Take VT 100 North to U.S. 4 and follow U.S. 4 west to Killington. The Greenbrier is located 6.5 miles after the first “Welcome to Killington” sign and 2/10ths of a mile after the second, on your right hand side." },
            { kind: 'h2', text: "From Montreal, approx. 3 hours" },
            { kind: 'p', text: "Take Highway 10 east to Highway 35 south, exit at Route 133 south and follow it to I-89 south. Leave I-89 at Exit 3 in Bethel, VT and follow VT 107 and VT 100 south to Killington. Make a right onto U.S. 4 west and continue for 1/10th of a mile. The Greenbrier, a large blue building, will be the first building on your right hand side." },
            { kind: 'h2', text: "GPS coordinates" },
            { kind: 'p', text: "Latitude 43.66751, longitude -72.80886. Whichever way you come, come down our driveway: the inn is located behind the gift shop. Welcome!" }
        ]
    },
    {
        slug: 'four-seasons-around-killington',
        title: "Four seasons of fun around Killington",
        seoTitle: "Four seasons around Killington",
        description: "Skiing five minutes away, golf two miles away, a summer pool, hiking trails and a trout stream: what to do in every season at the Greenbrier Inn.",
        category: "Local guide",
        date: "All year",
        image: '/images/greenbrier/golf-green-mountain-national.jpg',
        imageAlt: "Fairway and pond at Green Mountain National Golf Course",
        body: [
            { kind: 'p', text: "The Greenbrier Inn is centrally located right in the town of Killington, Vermont: five minutes from the Killington ski lifts and two miles from Killington's restaurants, bars and nightlife. Whatever the season, there is plenty to do close by." },
            { kind: 'h2', text: "Winter: ski the Beast of the East" },
            { kind: 'p', text: "Killington Mountain Resort is five miles from the inn, and we offer discounted Killington and Pico lift tickets to our guests with a room reservation. Back at the inn, go snowshoeing, then enjoy a cup of hot chocolate in our lounge by the fire." },
            { kind: 'h2', text: "Summer: golf, swimming and mountain biking" },
            { kind: 'p', text: "Green Mountain National Golf Course is two miles away, one of several golf courses around Killington. Our outdoor swimming pool is open in the summer, and mountain bikers can ride the Beast at Killington." },
            { kind: 'h2', text: "On our own trails" },
            { kind: 'p', text: "Right at the inn you will find hiking trails and a trout stream to take a hike along. Every room comes with a private balcony or patio, and mountain views from your room." },
            { kind: 'h2', text: "Shop before you go" },
            { kind: 'p', text: "Our 7,000 square foot home decor and gift shop sits at the front of the property, with jewelry, home decor and much, much more. Call the gift shop at 802-775-1575." }
        ]
    }
]

export const getArticle = (slug: string): Article | undefined =>
    articles.find((article) => article.slug === slug)
